import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTopology } from "@/store/site";
import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { MODE_CATALOG } from "@/data/modeCatalog";
import { carryTo } from "@/lib/carryParams";
import { useT } from "@/lib/i18n";
import { HelpHint } from "@/components/ui/HelpHint";
import { Cable, BatteryCharging, Sun, Cog, ShieldCheck, ChevronRight } from "lucide-react";

export default function SiteConfigPage() {
  const topo = useTopology();
  const t = useT();
  const location = useLocation();

  // Anchor target for every "canonical sign convention" mention across the
  // SPA (/site#sign-convention). BrowserRouter doesn't auto-scroll to hash
  // fragments, so do it ourselves once the card is in the DOM (CTL-09).
  useEffect(() => {
    if (location.hash === "#sign-convention") {
      document.getElementById("sign-convention")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  return (
    <PageShell title={t("manage.site.title")} caption={t("manage.site.caption", { site: topo.id, tz: topo.timezone })}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("manage.site.bessRoster")}</CardTitle>
            <span className="flex items-center gap-2 text-[10px] text-text-muted">
              {/* Same-page anchor (CTL-12): the invert flags below are the
                  per-unit half of the sign-convention story. */}
              <a href="#sign-convention" className="underline decoration-dotted underline-offset-2 hover:text-accent">{t("manage.site.signConvention")}</a>
              <span>{topo.bess.length === 1 ? t("manage.site.units.one", { count: 1 }) : t("manage.site.units.many", { count: topo.bess.length })}</span>
            </span>
          </CardHeader>
          <div className="divide-y divide-edge">
            {topo.bess.map((b) => (
              <Link
                key={b.id}
                to={carryTo(`/bess/${b.id}`)}
                title={t("manage.site.openDetail", { name: b.name })}
                className="flex items-start justify-between px-4 py-3 transition-colors hover:bg-surface-2"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <BatteryCharging size={14} className="text-charge" />
                    <strong>{b.name}</strong>
                    <span className="rounded-md border border-edge bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-muted">{b.family}</span>
                    <span className="rounded-md border border-edge bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-muted">{b.protocol}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-text-muted">
                    {b.host}:{b.port} · {b.capacity_kWh} kWh · ±{b.max_charge_kW} kW
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-text-faint">
                    {t("manage.site.signFixups")}
                    {/* Wrapper guard: HelpHint's backdrop/close clicks must not
                        navigate the surrounding roster Link (RightNowStrip pattern). */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <HelpHint title={t("manage.site.invertFlags")}>
                        {t("manage.site.invertFlagsBody")}
                      </HelpHint>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 text-[10px] text-text-muted">
                    <span>{t("manage.site.activeMeasInvert")} <strong className={b.invert.active_power_measurement ? "text-warn" : "text-text"}>{t(b.invert.active_power_measurement ? "manage.on" : "manage.off")}</strong></span>
                    <span>{t("manage.site.activeSetInvert")} <strong className={b.invert.active_power_setpoint ? "text-warn" : "text-text"}>{t(b.invert.active_power_setpoint ? "manage.on" : "manage.off")}</strong></span>
                    <span>{t("manage.site.reactiveMeasInvert")} <strong className={b.invert.reactive_power_measurement ? "text-warn" : "text-text"}>{t(b.invert.reactive_power_measurement ? "manage.on" : "manage.off")}</strong></span>
                    <span>{t("manage.site.reactiveSetInvert")} <strong className={b.invert.reactive_power_setpoint ? "text-warn" : "text-text"}>{t(b.invert.reactive_power_setpoint ? "manage.on" : "manage.off")}</strong></span>
                  </div>
                </div>
                <ChevronRight size={14} className="text-text-faint" />
              </Link>
            ))}
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("manage.site.smartMeters")}</CardTitle>
            <span className="flex items-center gap-2 text-[10px] text-text-muted">
              <a href="#sign-convention" className="underline decoration-dotted underline-offset-2 hover:text-accent">{t("manage.site.signConvention")}</a>
              <span>{topo.smartmeters.length === 1 ? t("manage.site.meters.one", { count: 1 }) : t("manage.site.meters.many", { count: topo.smartmeters.length })}</span>
            </span>
          </CardHeader>
          <div className="divide-y divide-edge">
            {topo.smartmeters.map((m) => (
              <div key={m.id} className="flex items-start justify-between px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    {m.role === "pv" ? <Sun size={14} className="text-solar" /> : <Cable size={14} className="text-import" />}
                    <strong>{m.name}</strong>
                    <span className="rounded-md border border-edge bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-muted">{m.role}</span>
                    {/* Non-default factor (≠1) is a deliberate per-site sign fixup —
                        emphasize it so a flipped CT is visible at a glance (CTL-12). */}
                    <span className={m.factor !== 1
                      ? "rounded-md border border-warn/40 bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold text-warn"
                      : "rounded-md border border-edge bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-muted"}>
                      {t("manage.site.factor", { value: m.factor })}
                    </span>
                    <HelpHint title={t("manage.site.meterFactor")}>
                      {t("manage.site.meterFactorBody")} {t("manage.site.seeThe.pre")}{" "}
                      <a href="#sign-convention" className="underline text-accent">{t("manage.site.signCard.link")}</a>{" "}
                      {t("manage.site.seeThe.post")}
                    </HelpHint>
                  </div>
                  <div className="mt-1 text-[11px] text-text-muted">{m.host}:{m.port} · {m.protocol}</div>
                  <div className="mt-1 text-[10px] text-text-faint">{t("manage.site.mqttTopic")} · <span className="font-mono">{m.topics.power_W}</span></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Cog size={16} className="text-accent" />
          <span className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("nav.controller")}</span>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field
            label={t("manage.site.gridMeter")}
            value={topo.smartmeters.find((m) => m.id === topo.controller.grid_smartmeter_id)?.name ?? "—"}
            to={carryTo("/historical", { keys: "grid_W", preset: "24h" })}
          />
          <Field label={t("manage.site.scheduleAttr")} value={topo.controller.schedule_attribute} mono />
          <Field label={t("manage.site.diagTopic")} value={topo.controller.diagnostic_topic} mono />
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.site.modesAvailable")}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {topo.controller.modes.map((m) => (
              // N21 — these chips are an INFORMATIONAL roster of supported modes
              // on a read-only config page; the link navigates to (not edits)
              // the schedule. Make the tooltip honest about the destination so
              // the chip never reads as "create a schedule entry" (the
              // auto-mint ?suggest= hand-off is reserved for action-framed
              // surfaces like Market's "Plan it"). The mode summary stays as
              // descriptive context after the destination verb.
              <Link
                key={m}
                to={carryTo("/schedule")}
                className="rounded-md border border-edge bg-surface-2 px-2 py-0.5 text-[10px] transition-colors hover:border-accent/40 hover:text-text"
                title={`${t("manage.site.viewSchedule")}${MODE_CATALOG[m]?.summary ? ` — ${MODE_CATALOG[m]!.summary}` : ""}`}
              >
                {MODE_CATALOG[m]?.label ?? m}
              </Link>
            ))}
          </div>
        </div>
      </Card>

      <Card id="sign-convention" className="scroll-mt-4 p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-accent" />
          <span className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.site.signConvention")}</span>
        </div>
        <p className="mt-1 text-xs text-text-muted">
          {t("manage.site.sign.canonical")}{" "}
          {t("manage.site.sign.grid")} <strong className="text-import">+</strong> {t("manage.site.sign.import")}{" "}
          {t("manage.site.sign.bess")} <strong className="text-charge">+</strong> {t("manage.site.sign.charging")}{" "}
          {t("manage.site.sign.controller")} <strong className="text-charge">+</strong> {t("manage.site.sign.chargeCmd")}{" "}
          {t("manage.site.sign.tailPre")} <code className="text-text">invert.*</code> {t("manage.site.sign.tailPost")}
        </p>
      </Card>
    </PageShell>
  );
}

function Field({ label, value, mono = false, to }: { label: string; value: string; mono?: boolean; to?: string }) {
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{label}</div>
      <div className={`mt-1 text-sm font-semibold ${mono ? "font-mono text-text" : "text-text"}`}>{value}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="block rounded-md border border-edge bg-surface-2 p-3 transition-colors hover:border-accent/40">
        {inner}
      </Link>
    );
  }
  return <div className="rounded-md border border-edge bg-surface-2 p-3">{inner}</div>;
}
